import { Helmet } from "react-helmet-async";

/**
 * SEO wrapper — manages title, meta description, OG, Twitter, canonical, JSON-LD schema, hreflang
 */
export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  twitterHandle = "@capellacave",
  noindex = false,
  jsonLd,
  hreflang = [],
  breadcrumbs,
  publishedAt,
  modifiedAt,
  author,
}) {
  const siteName = "Capella Cave";
  const fullTitle = title ? `${title} — ${siteName}` : `${siteName} — Kapadokya Journal`;
  const desc = description || "Kapadokya'nın editör kalemiyle: rehberler, vadiler, balon turları ve butik bir mağara otelin hikâyesi.";

  // Build absolute URLs for OG / canonical (required by social crawlers)
  // Always anchor to production canonical domain. Fall back to capellacave.com if env is missing
  // to prevent preview-URL leakage into social shares / search engines.
  const canonicalOrigin = process.env.REACT_APP_CANONICAL_URL || "https://capellacave.com";
  const previewOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const path = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
  const url = canonical || `${canonicalOrigin}${path}`;
  const rawImg = ogImage || "/media/img/og-default.jpg";
  const img = rawImg.startsWith("http") ? rawImg : `${canonicalOrigin}${rawImg}`;

  const breadcrumbLd = breadcrumbs && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.label,
      item: b.url,
    })),
  };

  return (
    <Helmet>
      <html lang="tr" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {url && <link rel="canonical" href={url} />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title || siteName} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="tr_TR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || siteName} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}

      {publishedAt && <meta property="article:published_time" content={publishedAt} />}
      {modifiedAt && <meta property="article:modified_time" content={modifiedAt} />}
      {author && <meta property="article:author" content={author} />}

      {/* Hreflang */}
      {hreflang.map((h) => (
        <link key={h.lang} rel="alternate" hrefLang={h.lang} href={h.url} />
      ))}

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
      {breadcrumbLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      )}
    </Helmet>
  );
}
